/**
 * Countries List Page — Matches production (tripnovac-visa.com/countries)
 * Hero banner, filter chips, photo cards with visa status badges, hearts, View Visa Options
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Card,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  IconButton,
} from '@mui/material';
import {
  Search,
  Public,
  FavoriteBorder,
  Favorite,
  FilterList,
  CheckCircle,
  Cancel,
  FlightTakeoff,
  AccessTime,
  VerifiedUser,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchCountries } from '../../utils/visaApi';
import type { Country } from '../../types/visa';

/* ── Visa-status helpers ─────────────────────────────────────── */
const getVisaStatus = (country: any): { label: string; color: string; bg: string; chipColor: string } => {
  const iso2 = (country.iso2 || '').toUpperCase();
  const schengen = ['AT','BE','CZ','DK','EE','FI','FR','DE','GR','HU','IS','IT','LV','LI','LT','LU','MT','NL','NO','PL','PT','SK','SI','ES','SE','CH'];
  if (schengen.includes(iso2)) return { label: 'Schengen', color: '#fff', bg: '#1565C0', chipColor: '#1565C0' };
  const eta = ['US','GB','CA','JP','KR','AU','NZ'];
  if (eta.includes(iso2)) return { label: 'ETA', color: '#fff', bg: '#00796B', chipColor: '#00796B' };
  const evisa = ['IN','KE','TZ','AL','TR','AZ','GE','KH','LK','ET','RW','ZM','ZW','UG','MM','LA'];
  if (evisa.includes(iso2)) return { label: 'eVisa', color: '#fff', bg: '#0288D1', chipColor: '#0288D1' };
  const visaFree = ['AR','BR','CR','ID','MY','MX','PH','SG','TH','CL','CO','PE','EC','PA','UY','PY','DO','JM','TT','BB','BS'];
  if (visaFree.includes(iso2)) return { label: 'Visa Free', color: '#fff', bg: '#2E7D32', chipColor: '#2E7D32' };
  return { label: 'Visa Required', color: '#fff', bg: '#D32F2F', chipColor: '#D32F2F' };
};

const getStatusIcon = (label: string) => {
  switch (label) {
    case 'Visa Free': return <CheckCircle sx={{ fontSize: 14 }} />;
    case 'Visa Required': return <Cancel sx={{ fontSize: 14 }} />;
    case 'eVisa': return <FlightTakeoff sx={{ fontSize: 14 }} />;
    case 'ETA': return <AccessTime sx={{ fontSize: 14 }} />;
    case 'Schengen': return <VerifiedUser sx={{ fontSize: 14 }} />;
    default: return null;
  }
};

const getCountryImage = (country: any): string => {
  const iso2 = (country.iso2 || country.code || '').toLowerCase();
  if (!iso2) return 'https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=400&h=300&fit=crop';
  return `https://flagcdn.com/w640/${iso2}.png`;
};

/* ── Component ───────────────────────────────────────────────── */
export const CountriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [selectedVisaStatus, setSelectedVisaStatus] = useState<string>('all');
  const [savedCountries, setSavedCountries] = useState<Set<string>>(new Set());

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await fetchCountries();
      setCountries(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const toggleSaved = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSavedCountries(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filteredCountries = countries.filter((country) => {
    const q = searchQuery.toLowerCase();
    const aliases = Array.isArray((country as any).aliases) ? (country as any).aliases : [];
    const matchesSearch = !q || country.name.toLowerCase().includes(q) ||
      (country.iso2 || '').toLowerCase().includes(q) ||
      (country.iso3 || '').toLowerCase().includes(q) ||
      aliases.some((alias: string) => String(alias).toLowerCase().includes(q));
    const matchesRegion = selectedRegion === 'all' || country.region === selectedRegion;
    const matchesVisa = selectedVisaStatus === 'all' || getVisaStatus(country).label === selectedVisaStatus;
    return matchesSearch && matchesRegion && matchesVisa;
  });

  const regions = Array.from(new Set(countries.map(c => c.region).filter(Boolean))).sort();
  const filterChips = ['Visa Free', 'Visa Required', 'eVisa', 'ETA', 'Schengen'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, bgcolor: '#030B1B' }}>
        <CircularProgress size={48} sx={{ color: '#0EA5E9' }} />
        <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.55)' }}>Loading countries...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center', bgcolor: '#030B1B', minHeight: '100vh' }}>
        <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(211,47,47,0.15)', color: '#ff6b6b', border: '1px solid rgba(211,47,47,0.3)' }}>{error}</Alert>
        <Button variant="contained" onClick={loadData} sx={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', '&:hover': { background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', filter: 'brightness(1.1)' } }}>Retry</Button>
      </Container>
    );
  }

  return (
    <Box sx={{ pb: 6, bgcolor: '#030B1B', minHeight: '100vh' }}>
      {/* ── Hero Banner ─────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          backgroundImage: 'url("https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          minHeight: 200,
          py: 5,
          px: 3,
          pb: 7,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(3,11,27,0.85) 0%, rgba(15,23,42,0.75) 50%, rgba(3,11,27,0.9) 100%)',
            zIndex: 0,
          },
          '& > *': { position: 'relative', zIndex: 1 },
        }}
      >
        <Public sx={{ fontSize: 48, opacity: 0.9, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))', color: '#0EA5E9' }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
            Explore Destinations
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
            {countries.length}+ countries with visa information at your fingertips
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg">
        {/* ── Filters Row ──────────────────────────────────── */}
        <Box sx={{
          display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center',
          p: 2, borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)',
        }}>
          <TextField
            placeholder="Search countries by name or ISO code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{
              flex: 1, minWidth: 240,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.06)',
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
              },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ color: 'rgba(255,255,255,0.4)' }} /></InputAdornment>,
            }}
          />
          <FormControl size="small" sx={{
            minWidth: 140,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255,255,255,0.06)',
              color: 'white',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
            },
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#0EA5E9' },
            '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
          }}>
            <InputLabel>Region</InputLabel>
            <Select label="Region" value={selectedRegion} onChange={(e: SelectChangeEvent) => setSelectedRegion(e.target.value)}>
              <MenuItem value="all">All Regions</MenuItem>
              {regions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{
            minWidth: 140,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255,255,255,0.06)',
              color: 'white',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
            },
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#0EA5E9' },
            '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
          }}>
            <InputLabel>Visa Status</InputLabel>
            <Select label="Visa Status" value={selectedVisaStatus} onChange={(e: SelectChangeEvent) => setSelectedVisaStatus(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              {filterChips.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <IconButton size="small" sx={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1, color: 'rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' } }}>
            <FilterList />
          </IconButton>
        </Box>

        {/* ── Filter Chips ─────────────────────────────────── */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {filterChips.map(chip => {
            const active = selectedVisaStatus === chip;
            const chipStatus = getVisaStatus({ iso2: chip === 'Visa Free' ? 'BR' : chip === 'eVisa' ? 'AL' : chip === 'ETA' ? 'US' : chip === 'Schengen' ? 'DE' : 'XX' });
            return (
              <Chip
                key={chip}
                icon={getStatusIcon(chip) || undefined}
                label={chip}
                size="small"
                onClick={() => setSelectedVisaStatus(active ? 'all' : chip)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  bgcolor: active ? chipStatus.bg : `${chipStatus.chipColor}15`,
                  color: active ? '#fff' : chipStatus.chipColor,
                  border: `1px solid ${active ? chipStatus.chipColor : chipStatus.chipColor + '40'}`,
                  '& .MuiChip-icon': { color: active ? '#fff' : chipStatus.chipColor },
                  cursor: 'pointer',
                  '&:hover': { bgcolor: active ? chipStatus.bg : `${chipStatus.chipColor}25` },
                }}
              />
            );
          })}
        </Box>

        <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.55)' }}>
          Showing {filteredCountries.length} of {countries.length} countries
        </Typography>

        {/* ── Country Photo Cards ──────────────────────────── */}
        <Grid container spacing={2.5}>
          {filteredCountries.map((country) => {
            const status = getVisaStatus(country);
            const iso3 = country.iso3 || country.iso2;
            const isSaved = savedCountries.has(country.id);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={country.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: 'rgba(255,255,255,0.03)',
                    overflow: 'hidden',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#0EA5E9',
                      boxShadow: '0 4px 24px rgba(14,165,233,0.15)',
                      transform: 'translateY(-2px)',
                      bgcolor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  {/* Photo with overlay */}
                  <Box
                    sx={{
                      position: 'relative',
                      height: 160,
                      backgroundImage: `url(${getCountryImage(country)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      bgcolor: '#1E293B',
                    }}
                  >
                    {/* Visa status badge */}
                    <Chip
                      icon={getStatusIcon(status.label) || undefined}
                      label={status.label}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        bgcolor: status.bg,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24,
                        '& .MuiChip-icon': { color: '#fff' },
                      }}
                    />
                    {/* Heart / Save */}
                    <IconButton
                      onClick={(e) => toggleSaved(country.id, e)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(4px)',
                        width: 32,
                        height: 32,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                      }}
                    >
                      {isSaved
                        ? <Favorite sx={{ fontSize: 18, color: '#E91E63' }} />
                        : <FavoriteBorder sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
                      }
                    </IconButton>
                    {/* Country name overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 1.5,
                        pt: 4,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                        {country.name}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Bottom area */}
                  <Box sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'rgba(255,255,255,0.55)' }}>{iso3}</Typography>
                    </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/countries/${iso3}`)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                        color: 'white',
                        '&:hover': { background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', filter: 'brightness(1.1)' },
                      }}
                    >
                      View Visa Options
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {filteredCountries.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Public sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              No countries found matching your criteria
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CountriesPage;